import type { TFunction } from 'i18next'

import { DEFAULT_CURRENCY } from '@features/invoices/constants/defaultCurrency'
import { DEFAULT_INVOICE_LANGUAGE } from '@features/invoices/constants/defaultInvoiceLanguage'
import { DEFAULT_INVOICE_PAYMENT_TERM } from '@features/invoices/constants/defaultInvoicePaymentTerm'
import { defaultInvoiceTextTranslationKeys } from '@features/invoices/constants/defaultInvoiceTextTranslationKeys'
import { invoiceLanguageToLocale } from '@features/invoices/constants/invoiceLanguageToLocale'
import { vatRateRelatedLineColumns } from '@features/invoices/constants/vatRateRelatedLineColumns'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import type { InvoiceLineColumn } from '@features/invoices/types/invoiceLineColumn'
import { getDefaultInvoiceLocale } from '@features/invoices/utils/getDefaultInvoiceLocale'
import { ProductType } from '@features/products/enums/productType'
import { isInvoiceProductType } from '@features/products/utils/isInvoiceProductType'
import type { VatRate } from '@features/vatRates/types/vatRate'

import type { ZervantSettings } from '../../../types/zervantSettings'
import type { FieldInterface } from '../../../types/zervantSettingsModel'
import { getSettingsFieldsByDocumentType } from '../../../utils/getSettingsFieldsByDocumentType'

interface GetInvoicingSettingsPreviewValuesOptions {
  isVatExempted?: boolean
  t: TFunction
  vatRate?: VatRate
  zervantSettings: ZervantSettings
}

// An empty default means the field has no default, the same way the invoice editor reads it.
const getDefaultValue = (field?: FieldInterface): string | undefined => field?.default || undefined

const getDefaultNumber = (value: string | undefined, fallback: number): number => {
  const numberValue = Number(value)

  return Number.isFinite(numberValue) ? numberValue : fallback
}

export const getInvoicingSettingsPreviewValues = (options: GetInvoicingSettingsPreviewValuesOptions) => {
  const { isVatExempted, t, vatRate, zervantSettings } = options
  const settingsFields = getSettingsFieldsByDocumentType(zervantSettings, InvoiceDocumentType.Invoice)
  const invoiceLocale =
    getDefaultInvoiceLocale(settingsFields.language?.default) ?? invoiceLanguageToLocale[DEFAULT_INVOICE_LANGUAGE]
  const defaultProductType = settingsFields.productType?.default
  const visibleColumns = (getDefaultValue(settingsFields.columns)?.split(',') ?? []) as InvoiceLineColumn[]

  return {
    previewValues: {
      currency: getDefaultValue(settingsFields.currency) ?? DEFAULT_CURRENCY,
      footerNote:
        getDefaultValue(settingsFields.footerText) ??
        t(defaultInvoiceTextTranslationKeys.footerNote, { lng: invoiceLocale }),
      language: invoiceLocale,
      message:
        getDefaultValue(settingsFields.description) ??
        t(defaultInvoiceTextTranslationKeys.message, { lng: invoiceLocale }),
      paymentTerm: getDefaultNumber(getDefaultValue(settingsFields.paymentTime), DEFAULT_INVOICE_PAYMENT_TERM),
      productType: isInvoiceProductType(defaultProductType) ? defaultProductType : ProductType.Goods,
      vatRateId: vatRate?.id,
      vatRatePercent: vatRate?.rate,
    },
    visibleColumns: isVatExempted
      ? visibleColumns.filter((column) => !vatRateRelatedLineColumns.includes(column))
      : visibleColumns,
  }
}
